import { useState } from "react";
import { joinWaitlist } from "../../../lib/content";
import type { SubscribePitch } from "../../../config/subscribePitch";
import { trackNewsletterSignup } from "../../../utils/track";

// Compact owned email capture for high-traffic surfaces (articles). Posts to the
// owned WaitlistEntry table via /api/waitlist (source-tagged), same as the
// /about Newsletter section — no third-party embed. Three variants: a full-width
// "card" for the end of an article, a narrow "rail" for the sticky sidebar, and
// "dark" for sitting inside a brand-navy section (the /learnings closing block),
// where a light card would read as a second, competing panel.
//
// `pitch` swaps the wording for the page's topic (see config/subscribePitch).
// It is optional on purpose: with no pitch every variant renders exactly the
// copy it always did, so callers that have no category are untouched.
export default function InlineSubscribe({
  source,
  variant = "card",
  pitch,
}: {
  source: string;
  variant?: "card" | "rail" | "dark";
  pitch?: SubscribePitch;
}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setError("");
    try {
      await joinWaitlist(email.trim(), source);
      // After the row exists, so a rejected address is never counted as a signup.
      trackNewsletterSignup(source);
      setStatus("done");
      setEmail("");
    } catch (err) {
      setError((err as Error).message);
      setStatus("error");
    }
  }

  if (variant === "dark") {
    return (
      <div>
        {status === "done" ? (
          <p className="font-body text-white/85">
            You&rsquo;re on the list. Thanks for subscribing.
          </p>
        ) : (
          <>
            <p className="font-body text-white/75">
              {pitch?.line ?? "Or just get the occasional email: notes on system design and shipping AI, no fixed schedule."}
            </p>
            <form onSubmit={submit} className="mx-auto mt-5 flex max-w-xl flex-col gap-3 sm:flex-row">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                aria-label="Email address"
                autoComplete="email"
                className="flex-1 rounded-smooth border border-gray-50/40 bg-white/10 px-4 py-3 font-body text-white placeholder:text-white/50 focus:border-gray-50/70 focus:outline-none focus:ring-2 focus:ring-gray-50/25"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="rounded-smooth border-2 border-gray-50/40 px-6 py-3 font-heading font-bold text-white transition-colors hover:border-gray-50/60 hover:bg-gray-50/10 disabled:opacity-60"
              >
                {status === "loading" ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
            {status === "error" ? <p className="mt-2 font-body text-sm text-white">{error}</p> : null}
            <p className="mt-2 font-body text-xs text-white/50">No spam. Unsubscribe anytime.</p>
          </>
        )}
      </div>
    );
  }

  if (variant === "rail") {
    return (
      <div>
        <p className="mb-1 font-heading text-overline uppercase tracking-[0.14em] text-brand-navy/45">Newsletter</p>
        {status === "done" ? (
          <p className="text-body-sm text-brand-navy/70">You&rsquo;re on the list. Thanks!</p>
        ) : (
          <form onSubmit={submit} className="space-y-2">
            <p className="text-body-sm leading-snug text-brand-navy/70">{pitch?.line ?? "Occasional notes on system design and shipping AI."}</p>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-label="Email address"
              autoComplete="email"
              className="w-full rounded-smooth border border-brand-navy/25 bg-white px-3 py-2 text-body-sm text-brand-navy focus:border-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full rounded-smooth bg-brand-navy px-3 py-2 font-heading text-body-sm font-bold text-white transition-colors hover:bg-brand-navy/90 disabled:opacity-60"
            >
              {status === "loading" ? "Subscribing…" : "Subscribe"}
            </button>
            {status === "error" ? <p className="text-caption text-brand-red">{error}</p> : null}
          </form>
        )}
      </div>
    );
  }

  return (
    <aside className="my-12 rounded-xl border border-brand-navy/15 bg-brand-navy/[0.03] p-6 sm:p-8">
      {status === "done" ? (
        <div>
          <p className="font-heading text-lg font-bold text-brand-navy">You&rsquo;re on the list.</p>
          <p className="mt-1 font-body text-brand-navy/70">Thanks for subscribing, keep an eye on your inbox.</p>
        </div>
      ) : (
        <>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-brand-navy">{pitch?.heading ?? "Get the occasional email"}</h2>
          <p className="mt-2 max-w-xl font-body text-brand-navy/70">
            {pitch?.line ?? "Notes on system design and shipping AI, delivered when there’s something worth keeping. No fixed schedule, no fluff."}
          </p>
          <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-label="Email address"
              autoComplete="email"
              className="flex-1 rounded-smooth border border-brand-navy/25 bg-white px-4 py-3 text-brand-navy focus:border-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="rounded-smooth bg-brand-navy px-6 py-3 font-heading font-bold text-white transition-colors hover:bg-brand-navy/90 disabled:opacity-60"
            >
              {status === "loading" ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
          {status === "error" ? <p className="mt-2 text-sm text-brand-red">{error}</p> : null}
          <p className="mt-2 text-xs text-brand-navy/50">No spam. Unsubscribe anytime.</p>
        </>
      )}
    </aside>
  );
}
